import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AffiliateDropdown from 'feature/dispatching/components/sidebar/AffiliateDropdown';
import ViewByDropdown from 'feature/dispatching/components/sidebar/ViewByDropdown';
import FiltersSkeleton from 'feature/dispatching/components/sidebar/FiltersSkeleton';
import MobileFilters from 'feature/dispatching/components/sidebar/MobileFilters';
import SelectorCard from 'feature/dispatching/components/sidebar/SelectorCard';
import SelectorCardCount from 'feature/dispatching/components/sidebar/SelectorCardCount';
import SelectorSkeleton from './SelectorSkeleton';
import { useViewDriverSummaryByDriverQuery } from '../driverStatusEndpoints';

const sidebarStyles = {
  bgcolor: 'surface.white',
  border: 1,
  borderBottom: 0,
  borderColor: 'graphic.light',
  borderLeft: 0,
  borderTop: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  pb: 2,
  position: 'fixed',
  // eslint-disable-next-line
  top: { xs: 56, sm: 64, md: 64, lg: 48 },
  // eslint-disable-next-line
  width: { xs: 1, sm: 1, md: 300, lg: 300 },
};

const scrollbarStyles = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  overflowY: 'scroll',
  pb: 4,
  scrollbarWidth: 'none',
};

const ViewSummaryByLocation = () => {
  const navigate = useNavigate();
  const { viewById } = useParams();
  const mdAndDown = theme => theme.breakpoints.down('md');
  const mdDown = useMediaQuery(mdAndDown, { noSsr: true });
  const [selectorsFilter, setSelectorsFilter] = useState('');

  const equalsViewById = R.equals(viewById);

  const { data = [], isLoading } = useViewDriverSummaryByDriverQuery();

  // prettier-ignore
  const driverFilter = R.compose(
    R.includes(R.toUpper(selectorsFilter)),
    R.toUpper,
    ({firstName, lastName}) => firstName + lastName,
  )
  const filteredDrivers = data.filter(driverFilter);

  // prettier-ignore
  const handleSelectorsFilterChange = R.compose(
    setSelectorsFilter,
    R.path(['target', 'value'])
  );

  // prettier-ignore
  const handleSelectorClick = R.curryN(2, R.compose(
    navigate,
    R.concat('../compliance/driver-status/driver/'),
  ));

  // prettier-ignore
  const handleAffiliateChange = R.compose(
    setSelectorsFilter,
    R.always('')
  );

  useEffect(() => {
    // prettier-ignore
    const autoSelectFirst = R.and(
      R.not(mdDown), R.and(
      R.isNil(viewById),
      R.gt(data.length, 0)
    ));

    if (autoSelectFirst) {
      const [{ driverId }] = data;
      navigate(`../compliance/driver-status/driver/${driverId}`, { replace: true });
    }
  }, [data, mdDown, navigate, viewById]);

  const filters = (
    <Stack gap={2} sx={{ px: 2, py: 3 }}>
      <AffiliateDropdown onChange={handleAffiliateChange} />
      <ViewByDropdown module="compliance/driver-status">
        <MenuItem value="driver">Driver</MenuItem>
        <MenuItem value="location">Location</MenuItem>
      </ViewByDropdown>
      <TextField
        label="Driver Name"
        onChange={handleSelectorsFilterChange}
        value={selectorsFilter}
      />
    </Stack>
  );

  if (isLoading)
    return (
      <Box sx={sidebarStyles}>
        {mdDown ? (
          <MobileFilters>
            <FiltersSkeleton />
          </MobileFilters>
        ) : (
          <FiltersSkeleton />
        )}
        <Divider />
        <SelectorSkeleton />
        <SelectorSkeleton />
        <SelectorSkeleton />
      </Box>
    );

  return (
    <Box sx={sidebarStyles}>
      {mdDown ? <MobileFilters>{filters}</MobileFilters> : filters}
      <Divider />
      <Box sx={scrollbarStyles}>
        {filteredDrivers.map(
          ({ driverId, firstName, lastName, activeLocationsCount, inactiveLocationsCount }) => (
            <SelectorCard
              key={driverId}
              onClick={handleSelectorClick(driverId)}
              selected={equalsViewById(driverId)}
              title={`${R.toUpper(lastName)}, ${R.toUpper(firstName)}`}
            >
              <SelectorCardCount isActive label="Active Locations" value={activeLocationsCount} />
              <SelectorCardCount label="Inactive Locations" value={inactiveLocationsCount} />
            </SelectorCard>
          )
        )}
      </Box>
    </Box>
  );
};

export default ViewSummaryByLocation;
